import {
  callFetchRefreshTokenApi,
  callFetchTokenApi,
  callFetchUserDataApi,
} from './utils/api-calls.js';

/**
 * @typedef {import('../../shared/index.d.ts').ApiUserData} ApiUserData
 * @typedef {import('../../shared/index.d.ts').UserToken} UserToken
 * @typedef {import('../../shared/index.d.ts').DataQueueCallback} DataQueueCallback
 * @typedef {import('../../shared/index.d.ts').DodoUserAgentUserApi} DodoUserAgentUserApi
 */

/**
 * @implements {DodoUserAgentUserApi}
 */
export class DodoUser {
  /**
   *
   * @type {ApiUserData}
   */
  #data;

  /**
   * @type {Array<DataQueueCallback>}
   */
  #dataQueue = [];

  #dataRequestRunning = false;

  /**
   *
   * @type {UserToken | null}
   */
  #cachedToken = null;

  constructor() {
    this.getToken();
  }

  /**
   *
   * @returns {Promise<ApiUserData>}
   */
  async #fetchData() {
    try {
      const data = await callFetchUserDataApi();
      this.#data = data;

      return this.#data;
    } catch (error) {
      console.error('Could not fetch user data', error);
      throw error;
    }
  }

  /**
   *
   * @param {Number} nowTimestamp
   * @param {string} expiredAt
   * @returns {boolean}
   */
  static isTokenValid(nowTimestamp, expiredAt) {
    const expiredDate = new Date(expiredAt);
    const expiredTimestamp = expiredDate.getTime();
    const diff = expiredTimestamp - nowTimestamp;
    const seconds = diff / 1000;
    const minutes = seconds / 60;

    if (minutes < 2) {
      return false;
    }

    return true;
  }

  /**
   *
   * @returns {UserToken|null}
   */
  #getCachedToken() {
    if (!this.#cachedToken) {
      return null;
    }
    const { expiredAt } = this.#cachedToken;
    const nowTimestamp = Date.now();
    if (DodoUser.isTokenValid(nowTimestamp, expiredAt) === false) {
      this.#cachedToken = null;
      return null;
    }

    return this.#cachedToken;
  }

  /**
   *
   * @returns {Promise<UserToken>}
   */
  async #fetchToken() {
    const data = await callFetchTokenApi();
    this.#cachedToken = data;
    return {
      ...data,
    };
  }

  /**
   *
   * @returns {Promise<ApiUserData>}
   */
  getData() {
    return new Promise((resolve, reject) => {
      if (this.#data) {
        resolve(this.#data);
        return;
      }

      if (this.#dataRequestRunning) {
        this.#dataQueue.push([resolve, reject]);
        return;
      }

      this.#dataRequestRunning = true;
      this.#fetchData()
        .then((data) => {
          this.#dataRequestRunning = false;
          resolve(data);
          this.#dataQueue.forEach(([res]) => res(data));
          this.#dataQueue = [];
        })
        .catch((error) => {
          this.#dataRequestRunning = false;
          reject(error);
          this.#dataQueue.forEach(([, rej]) => rej(error));
          this.#dataQueue = [];
        });
    });
  }

  /**
   *
   * @returns {Promise<UserToken>}
   */
  async refreshToken() {
    const data = await callFetchRefreshTokenApi();
    this.#cachedToken = data;
    return {
      ...data,
    };
  }

  /**
   *
   * @returns {Promise<UserToken>}
   */
  async getToken() {
    const cached = this.#getCachedToken();
    if (cached) {
      // Return copy so no one can override it.
      return {
        ...cached,
      };
    }

    return this.#fetchToken();
  }
}
